import React from 'react';
import { Input, Select, cn } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiUser } = FiIcons;

function Toggle({ label, checked, onChange, disabled }) {
  return (
    <label className={cn('flex items-center justify-between gap-2 text-sm', disabled && 'opacity-50')}>
      <span className="text-gray-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-gray-200',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}

function ColorField({ label, value, onChange, disabled }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-10 w-12 rounded border border-gray-300 cursor-pointer disabled:opacity-50"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm font-mono"
        />
      </div>
    </div>
  );
}

function RepeatableList({
  items,
  onChange,
  disabled,
  fields,
  renderItem,
}) {
  const updateItem = (index, patch) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange(next);
  };

  const removeItem = (index) => onChange(items.filter((_, i) => i !== index));

  const addItem = () => {
    const blank = {};
    fields.forEach((f) => { blank[f.key] = f.defaultValue ?? ''; });
    onChange([...items, blank]);
  };

  const moveItem = (index, dir) => {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="rounded-md border border-gray-200 p-3 space-y-2 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Item {index + 1}</span>
            <div className="flex gap-1">
              <button type="button" disabled={disabled || index === 0} onClick={() => moveItem(index, -1)} className="p-1 text-gray-400 hover:text-primary disabled:opacity-30">
                <SafeIcon icon={FiChevronUp} className="w-4 h-4" />
              </button>
              <button type="button" disabled={disabled || index === items.length - 1} onClick={() => moveItem(index, 1)} className="p-1 text-gray-400 hover:text-primary disabled:opacity-30">
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </button>
              <button type="button" disabled={disabled} onClick={() => removeItem(index)} className="p-1 text-gray-400 hover:text-red-600">
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
          </div>
          {renderItem(item, (patch) => updateItem(index, patch))}
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={addItem}
        className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark disabled:opacity-50"
      >
        <SafeIcon icon={FiPlus} className="w-4 h-4" />
        Add item
      </button>
    </div>
  );
}

const AUTHOR_FIELD_GROUPS = [
  {
    title: 'Personal',
    fields: [
      { key: 'headshot', label: 'Headshot' },
      { key: 'name', label: 'Name' },
      { key: 'title', label: 'Title' },
      { key: 'bio', label: 'Bio' },
      { key: 'preferred_quote', label: 'Quote' },
    ],
  },
  {
    title: 'Results',
    fields: [
      { key: 'best_income_milestone', label: 'Best Income' },
      { key: 'years_in_industry', label: 'Years in Industry' },
      { key: 'clients_served', label: 'Clients Served' },
      { key: 'designations', label: 'Designations' },
      { key: 'awards', label: 'Awards' },
    ],
  },
  {
    title: 'Skills',
    fields: [{ key: 'skills', label: 'Skills & Qualities' }],
  },
  {
    title: 'Social',
    fields: [
      { key: 'facebook_url', label: 'Facebook' },
      { key: 'instagram_url', label: 'Instagram' },
      { key: 'linkedin_url', label: 'LinkedIn' },
      { key: 'youtube_url', label: 'YouTube' },
      { key: 'tiktok_url', label: 'TikTok' },
    ],
  },
  {
    title: 'CTA',
    fields: [
      { key: 'personal_cta_text', label: 'Personal CTA Text' },
      { key: 'personal_booking_url', label: 'Booking Button' },
    ],
  },
];

function FieldGroup({ title, children }) {
  return (
    <div className="rounded-md border border-gray-200 p-3 space-y-2 bg-gray-50/40">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function BlockEditor({ block, steps, users, onChange, disabled }) {
  if (!block) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6 text-gray-400">
        <p className="text-sm">Select a block to edit its content</p>
      </div>
    );
  }

  const content = block.content || {};
  const patch = (updates) => onChange({ ...content, ...updates });

  const stepOptions = (steps ?? []).map((s) => ({
    value: String(s.step_number),
    label: `Step ${s.step_number}: ${s.title}`,
  }));

  const userOptions = [
    { value: '', label: 'Select agent…' },
    ...(users ?? []).map((u) => ({ value: u.id, label: u.full_name || 'Agent' })),
  ];

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      <div className="pb-2 border-b border-gray-100">
        <p className="text-xs font-semibold text-primary uppercase tracking-wide">{block.block_type}</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">Block settings</p>
      </div>

      {block.block_type === 'hero' && (
        <>
          <Input label="Headline" value={content.headline || ''} onChange={(e) => patch({ headline: e.target.value })} disabled={disabled} />
          <Input label="Subheadline" value={content.subheadline || ''} onChange={(e) => patch({ subheadline: e.target.value })} disabled={disabled} />
          <ColorField label="Background color" value={content.background_color} onChange={(v) => patch({ background_color: v })} disabled={disabled} />
          <ColorField label="Text color" value={content.text_color} onChange={(v) => patch({ text_color: v })} disabled={disabled} />
          <Input label="Logo URL" value={content.logo_url || ''} onChange={(e) => patch({ logo_url: e.target.value })} disabled={disabled} />
        </>
      )}

      {block.block_type === 'vsl' && (
        <>
          <Input label="Video URL" value={content.video_url || ''} onChange={(e) => patch({ video_url: e.target.value })} disabled={disabled} placeholder="YouTube or Vimeo URL" />
          <Toggle label="Autoplay" checked={Boolean(content.autoplay)} onChange={(v) => patch({ autoplay: v })} disabled={disabled} />
          <Input label="Thumbnail URL" value={content.thumbnail_url || ''} onChange={(e) => patch({ thumbnail_url: e.target.value })} disabled={disabled} />
          <Input label="Caption" value={content.caption || ''} onChange={(e) => patch({ caption: e.target.value })} disabled={disabled} />
        </>
      )}

      {block.block_type === 'cta' && (
        <>
          <Input label="Button text" value={content.button_text || ''} onChange={(e) => patch({ button_text: e.target.value })} disabled={disabled} />
          <Toggle label="Use booking widget" checked={Boolean(content.use_booking)} onChange={(v) => patch({ use_booking: v })} disabled={disabled} />
          {!content.use_booking && (
            <>
              <Input label="URL" value={content.url || ''} onChange={(e) => patch({ url: e.target.value })} disabled={disabled} />
              <Select
                label="Target step"
                value={content.target_step != null ? String(content.target_step) : ''}
                onChange={(e) => patch({ target_step: e.target.value ? Number(e.target.value) : null })}
                options={[{ value: '', label: 'Select step…' }, ...stepOptions]}
                disabled={disabled}
              />
            </>
          )}
          <ColorField label="Background color" value={content.background_color} onChange={(v) => patch({ background_color: v })} disabled={disabled} />
          <ColorField label="Text color" value={content.text_color} onChange={(v) => patch({ text_color: v })} disabled={disabled} />
        </>
      )}

      {block.block_type === 'faq' && (
        <RepeatableList
          items={content.items || []}
          onChange={(items) => patch({ items })}
          disabled={disabled}
          fields={[{ key: 'question' }, { key: 'answer' }]}
          renderItem={(item, update) => (
            <>
              <Input label="Question" value={item.question || ''} onChange={(e) => update({ question: e.target.value })} disabled={disabled} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                <textarea
                  value={item.answer || ''}
                  onChange={(e) => update({ answer: e.target.value })}
                  disabled={disabled}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
            </>
          )}
        />
      )}

      {block.block_type === 'testimonials' && (
        <RepeatableList
          items={content.items || []}
          onChange={(items) => patch({ items })}
          disabled={disabled}
          fields={[{ key: 'name' }, { key: 'result' }, { key: 'detail' }]}
          renderItem={(item, update) => (
            <>
              <Input label="Name" value={item.name || ''} onChange={(e) => update({ name: e.target.value })} disabled={disabled} />
              <Input label="Result" value={item.result || ''} onChange={(e) => update({ result: e.target.value })} disabled={disabled} />
              <Input label="Detail" value={item.detail || ''} onChange={(e) => update({ detail: e.target.value })} disabled={disabled} />
            </>
          )}
        />
      )}

      {block.block_type === 'steps' && (
        <>
          <Input label="Headline" value={content.headline || ''} onChange={(e) => patch({ headline: e.target.value })} disabled={disabled} />
          <RepeatableList
            items={content.items || []}
            onChange={(items) => patch({ items })}
            disabled={disabled}
            fields={[{ key: 'number', defaultValue: 1 }, { key: 'title' }, { key: 'description' }]}
            renderItem={(item, update) => (
              <>
                <Input label="Number" type="number" value={item.number ?? ''} onChange={(e) => update({ number: Number(e.target.value) })} disabled={disabled} />
                <Input label="Title" value={item.title || ''} onChange={(e) => update({ title: e.target.value })} disabled={disabled} />
                <Input label="Description" value={item.description || ''} onChange={(e) => update({ description: e.target.value })} disabled={disabled} />
              </>
            )}
          />
        </>
      )}

      {block.block_type === 'booking' && (
        <>
          <Input label="Title" value={content.title || ''} onChange={(e) => patch({ title: e.target.value })} disabled={disabled} />
          <Input label="Subtitle" value={content.subtitle || ''} onChange={(e) => patch({ subtitle: e.target.value })} disabled={disabled} />
          <Select
            label="Assignment mode"
            value={content.assignment_mode || 'round_robin'}
            onChange={(e) => patch({ assignment_mode: e.target.value })}
            options={[
              { value: 'round_robin', label: 'Round robin' },
              { value: 'specific', label: 'Specific agent' },
            ]}
            disabled={disabled}
          />
          {content.assignment_mode === 'specific' && (
            <Select
              label="Assigned agent"
              value={content.assigned_user_id || ''}
              onChange={(e) => patch({ assigned_user_id: e.target.value || null })}
              options={userOptions}
              disabled={disabled}
            />
          )}
        </>
      )}

      {block.block_type === 'author' && (() => {
        const visibleFields = content.visible_fields || {};
        const selectedAgent = (users ?? []).find((u) => u.id === content.agent_id);

        const setVisibleField = (key, checked) => {
          patch({
            visible_fields: {
              ...visibleFields,
              [key]: checked,
            },
          });
        };

        return (
          <>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Selection</p>
              <Select
                label="Agent"
                value={content.agent_id || ''}
                onChange={(e) => patch({ agent_id: e.target.value || null })}
                options={userOptions}
                disabled={disabled}
              />
              {selectedAgent && (
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-white p-3">
                  {selectedAgent.headshot_url ? (
                    <img
                      src={selectedAgent.headshot_url}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      decoding="async"
                      className="h-12 w-12 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedAgent.full_name || 'Agent'}
                    </p>
                    {selectedAgent.title && (
                      <p className="text-xs text-gray-500">{selectedAgent.title}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Visible Fields</p>
              {AUTHOR_FIELD_GROUPS.map((group) => (
                <FieldGroup key={group.title} title={group.title}>
                  {group.fields.map((field) => (
                    <Toggle
                      key={field.key}
                      label={field.label}
                      checked={visibleFields[field.key] !== false}
                      onChange={(v) => setVisibleField(field.key, v)}
                      disabled={disabled}
                    />
                  ))}
                </FieldGroup>
              ))}
            </div>

            <div className="space-y-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Layout</p>
              <div className="space-y-2">
                {[
                  { value: 'card', label: 'Card', hint: 'Photo left, info right' },
                  { value: 'centered', label: 'Centered', hint: 'Photo top center, info below' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors',
                      content.layout === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300',
                      disabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <input
                      type="radio"
                      name="author-layout"
                      value={opt.value}
                      checked={content.layout === opt.value}
                      onChange={() => patch({ layout: opt.value })}
                      disabled={disabled}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
